










































import { Vue, Component, Prop, Ref, Watch } from "vue-property-decorator";
import ToolMap from "@/components/Tools/Map.vue";
import { GOOGLE_MAPS_API_KEY } from "../../utils/Constants";
import { v4 as uuidv4 } from "uuid";
import { Area, Marker, Polygon } from "@/models/MapObjects";
import { Position } from "@/models/Position";
import { md5 } from "@/utils/Md5";

declare global {
  interface Window {
    google?: any;
  }
}

Component.registerHooks(["beforeRouteEnter", "beforeRouteUpdate"]);

@Component({
  components: { ToolMap },
})
export default class Map extends Vue {
  @Ref("toolMap") readonly toolMap!: ToolMap;

  private markerString = "";
  private multipolygonString = "";
  private md5Objects: string[] = [];

  private googleMapsLoaded = false;
  private detailsType = "";
  private isOpen = true;

  async mounted() {
    await this.loadGoogleMaps();
    this.markerString = "POINT (9.2112868 45.4741489)";
    this.multipolygonString =
      "MULTIPOLYGON (((9.963231 45.621251, 9.963611 45.617944, 9.963788 45.61763, 9.968043 45.617435, 9.968288 45.617443, 9.968533 45.617452, 9.968557 45.617454, 9.968908 45.617514, 9.969259 45.617573, 9.971299 45.61854, 9.9713 45.6189, 9.969644 45.620222, 9.969463 45.620331, 9.969125 45.620454, 9.968014 45.620849, 9.963803 45.62157, 9.963455 45.621533, 9.963231 45.621251)), ((10.046432 45.630493, 10.046455 45.630134, 10.048857 45.629467, 10.049208 45.629388, 10.04962 45.629318, 10.054029 45.63201, 10.055281 45.633032, 10.055336 45.633388, 10.05321 45.635189, 10.052919 45.635401, 10.050962 45.635128, 10.05008 45.634881, 10.048485 45.633945, 10.046432 45.630493)), ((10.004251 45.646123, 10.005513 45.64387, 10.0058 45.643654, 10.010898 45.642792, 10.011116 45.642857, 10.011933 45.643214, 10.012269 45.64349, 10.013702 45.645288, 10.013685 45.645327, 10.01354 45.645657, 10.011018 45.645296, 10.006738 45.647539, 10.006408 45.647683, 10.004275 45.646482, 10.004251 45.646123)), ((10.044291 45.662752, 10.046014 45.65854, 10.046184 45.658223, 10.049751 45.656227, 10.050072 45.656063, 10.050329 45.65594, 10.050669 45.655823, 10.0525 45.656007, 10.053217 45.65616, 10.054508 45.658513, 10.054546 45.658871, 10.05405 45.660052, 10.051822 45.66123, 10.05026 45.661894, 10.04456 45.662991, 10.044291 45.662752)), ((9.958281881463414 45.66473524031674, 9.95916 45.66377, 9.960624 45.661655, 9.960438 45.661347, 9.958012 45.659576, 9.956952 45.659165, 9.954939 45.658654, 9.95444 45.658801, 9.954115 45.658954, 9.953664 45.65922, 9.953536 45.659557, 9.95323 45.661895, 9.953379 45.662351, 9.954981686871001 45.664743394148196, 9.954536896157535 45.66535892308796, 9.951033 45.667302, 9.950895 45.667635, 9.952898 45.669307, 9.953174 45.669537, 9.955112 45.67035, 9.955409 45.670381, 9.956344733331527 45.6704132819155, 9.957142 45.670605, 9.957502 45.670608, 9.95861 45.670519, 9.961041262065207 45.66932406340013, 9.963753 45.669459, 9.963973 45.669452, 9.964256 45.669331, 9.964307 45.669309, 9.965722 45.668449, 9.965957 45.668177, 9.965349 45.6676, 9.965211 45.667477, 9.961711 45.665991, 9.960229108322414 45.66577137302627, 9.959983 45.665433, 9.958351391291107 45.664779102035936, 9.958281881463414 45.66473524031674)))";
  }

  get scriptUrl() {
    return `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=it`;
  }

  async loadGoogleMaps() {
    if (!window.google?.maps?.places) {
      const plugin = document.createElement("script");
      plugin.setAttribute("src", this.scriptUrl);
      plugin.async = true;
      document.head.appendChild(plugin);
      await new Promise((resolve) => (plugin.onload = resolve));
    }
    this.googleMapsLoaded = true;
  }

  private generateId() {
    return uuidv4();
  }

  private objectAlreadyExists(originalInput: string) {
    const currentMd5 = md5(originalInput);
    const found = this.md5Objects.find((o) => o === currentMd5);
    if (!found) this.md5Objects.push(currentMd5);

    return found;
  }

  private buildMarkerFromString(input: string) {
    const str = input.substring(input.indexOf("(") + 1, input.indexOf(")"));
    const lng = str.substring(0, str.indexOf(" "));
    const lat = str.substring(str.indexOf(" ") + 1);
    const marker: Marker = new Marker();
    marker.id = this.generateId();
    marker.position = { lat: parseFloat(lat), lng: parseFloat(lng) };
    return marker;
  }

  private buildAreasFromString(input: string) {
    const str = input.substring(
      input.indexOf("(") + 1,
      input.indexOf(")))") + 2
    );
    const polygons: string[] = str.split("))");

    const areas: Area[] = [];
    for (let polygon of polygons) {
      const subpolygons: string[] = polygon.split("),");
      const area = new Area();
      area.paths = [];
      area.id = this.generateId();
      for (let subpolygon of subpolygons) {
        subpolygon = subpolygon.replace(/[(]+/gm, "");
        subpolygon = subpolygon.replace(/[)]+/gm, "");

        const coordinates: string[] = subpolygon.split(",");
        const pol: any[] = [];
        for (let coordinate of coordinates) {
          coordinate = coordinate.trim();
          const lng = coordinate.substring(0, coordinate.indexOf(" "));
          const lat = coordinate.substring(coordinate.indexOf(" ") + 1);
          if (!lng || !lat) continue;
          pol.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
        area.paths.push(pol);
      }
      areas.push(area);
    }

    return areas;
  }

  private addMarker() {
    if (this.objectAlreadyExists(this.markerString)) return null;
    const marker: Marker = this.buildMarkerFromString(this.markerString);
    this.toolMap.addMarker(marker);
    this.toolMap.centerAllObjects();
  }

  private addArea() {
    const cleanStr = this.cleanString(this.multipolygonString);
    if (this.objectAlreadyExists(cleanStr)) return null;
    const areas: Area[] = this.buildAreasFromString(cleanStr);
    for (const area of areas) {
      this.toolMap.addArea(area);
    }
    this.toolMap.centerAllObjects();
  }

  private removeMapObjects() {
    this.md5Objects = [];
    this.toolMap.removeAllObjects();
  }

  private cleanString(input: string) {
    return input.replace(/[\r\n]+/gm, "");
  }
}
